import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload,
  FileText,
  Check,
  X,
  AlertCircle,
  Loader2,
  RefreshCw,
  Database,
  FileSpreadsheet,
  Clock,
  CheckCircle,
  XCircle,
  Info,
  Eye,
  Download,
  ChevronRight,
  ChevronDown
} from 'lucide-react';

import { getApiBaseUrl } from '../../utils/apiUrl';
const API_URL = getApiBaseUrl();
const MAX_FILE_SIZE_MB = 50;

const CsvImportManager = () => {
  const [files, setFiles] = useState([]);
  const [importHistory, setImportHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importAllLoading, setImportAllLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importType, setImportType] = useState('auto');
  const [clearExisting, setClearExisting] = useState(false);
  
  // Phase 2: Preview and Progress states
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [importProgress, setImportProgress] = useState(null);
  const [importStatus, setImportStatus] = useState('idle'); // idle, uploading, validating, importing, completed, failed
  
  const fileInputRef = useRef(null);
  const xhrRef = useRef(null);

  const getAuthToken = () => {
    return localStorage.getItem('adminToken') || '';
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/pokemon-admin/import/files`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setFiles(data.success ? data.data : data);
      } else {
        showNotification('Failed to fetch files', 'error');
      }
    } catch (error) {
      console.error('Failed to fetch files:', error);
      showNotification('Network error fetching files', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/pokemon-admin/import/history`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setImportHistory(data.success ? data.data : data);
      } else {
        showNotification('Failed to fetch import history', 'error');
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
      showNotification('Network error fetching import history', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFiles();
    fetchHistory();
  }, [fetchFiles, fetchHistory]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (file) => {
    if (!file) {
      setSelectedFile(null);
      setPreviewData(null);
      setShowPreview(false);
      return;
    }

    // Client-side validation
    if (!file.name.endsWith('.csv')) {
      showNotification('Invalid file type. Only CSV files are allowed.', 'error');
      setSelectedFile(null);
      return;
    }

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      showNotification(`File too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`, 'error');
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
    setPreviewData(null);
    setShowPreview(false);
    
    // Auto-detect type
    const fileName = file.name.toLowerCase();
    if (fileName.includes('gigantamax')) {
      setImportType('pokemon_gigantamax');
    } else if (fileName.includes('mega')) {
      setImportType('pokemon_mega');
    } else if (fileName.includes('normal')) {
      setImportType('pokemon_normal');
    } else if (fileName.includes('max_counter')) {
      setImportType('max_counters');
    } else if (fileName.includes('max_defender')) {
      setImportType('max_defenders');
    } else if (fileName.includes('raid_counter')) {
      setImportType('raid_counters');
    } else {
      setImportType('auto');
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  // Phase 2: Preview CSV before import
  const handlePreview = async () => {
    if (!selectedFile) {
      showNotification('Please select a file first', 'error');
      return;
    }

    setPreviewLoading(true);
    setShowPreview(false);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('type', importType);

      const response = await fetch(`${API_URL}/api/pokemon-admin/import/preview`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setPreviewData(data.data);
        setShowPreview(true);
        // Update import type if auto-detected
        if (data.data.fileType && importType === 'auto') {
          setImportType(data.data.fileType);
        }
      } else {
        showNotification(data.message || data.error || 'Failed to preview CSV file', 'error');
      }
    } catch (error) {
      console.error('Error previewing CSV:', error);
      showNotification('Network error previewing CSV file', 'error');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Phase 2: Import with progress tracking
  const handleImport = async () => {
    if (!selectedFile) {
      showNotification('Please select a file first', 'error');
      return;
    }

    // Reset progress
    setUploadProgress(0);
    setImportProgress(null);
    setImportStatus('uploading');

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('type', importType);
      formData.append('clearExisting', clearExisting.toString());

      // Use XMLHttpRequest for upload progress
      const xhr = new XMLHttpRequest();
      xhrRef.current = xhr;

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          setUploadProgress(percentComplete);
        }
      });

      xhr.addEventListener('load', async () => {
        if (xhr.status === 200) {
          setImportStatus('validating');
          const data = JSON.parse(xhr.responseText);
          
          if (data.success) {
            setImportStatus('importing');
            setImportProgress({
              processed: data.stats?.importedRows || 0,
              total: data.stats?.totalRows || 0,
              imported: data.stats?.importedRows || 0,
              failed: data.stats?.failedRows || 0,
              skipped: data.stats?.skippedRows || 0
            });
            
            setTimeout(() => {
              setImportStatus('completed');
              showNotification(
                `Import completed: ${data.stats?.importedRows || 0}/${data.stats?.totalRows || 0} rows imported`,
                'success'
              );
              setSelectedFile(null);
              setPreviewData(null);
              setShowPreview(false);
              fetchFiles();
              fetchHistory();
            }, 1000);
          } else {
            setImportStatus('failed');
            showNotification(data.message || data.error || 'Import failed', 'error');
          }
        } else {
          setImportStatus('failed');
          const errorData = JSON.parse(xhr.responseText || '{}');
          showNotification(errorData.message || errorData.error || 'Import failed', 'error');
        }
      });

      xhr.addEventListener('error', () => {
        setImportStatus('failed');
        showNotification('Network error during import', 'error');
      });

      xhr.open('POST', `${API_URL}/api/pokemon-admin/import/csv`);
      xhr.setRequestHeader('Authorization', `Bearer ${getAuthToken()}`);
      xhr.send(formData);

    } catch (error) {
      console.error('Error importing CSV:', error);
      setImportStatus('failed');
      showNotification('Failed to import CSV file', 'error');
    }
  };

  const handleImportAll = async () => {
    if (!window.confirm('Import all CSV files from the data directory? This may take a while.')) {
      return;
    }

    setImportAllLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/pokemon-admin/import/all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify({ clearExisting: clearExisting.toString() })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        showNotification('All imports completed successfully!', 'success');
        fetchHistory();
      } else {
        showNotification(data.message || data.error || 'Import failed', 'error');
      }
    } catch (error) {
      console.error('Error importing all CSVs:', error);
      showNotification('Network error importing all files', 'error');
    } finally {
      setImportAllLoading(false);
    }
  };

  const handleDownloadErrorReport = async (logId) => {
    try {
      const response = await fetch(`${API_URL}/api/pokemon-admin/import/errors/${logId}?format=csv`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `import-errors-${logId}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        showNotification('Failed to download error report', 'error');
      }
    } catch (error) {
      console.error('Error downloading error report:', error);
      showNotification('Network error downloading error report', 'error');
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      'pokemon_normal': 'Pokemon Data',
      'pokemon_gigantamax': 'Gigantamax Pokemon',
      'pokemon_mega': 'Mega Pokemon',
      'max_counters': 'Max Battle Counters',
      'max_defenders': 'Max Battle Defenders',
      'raid_counters': 'Raid Counters',
      'unknown': 'Unknown Type'
    };
    return labels[type] || type;
  };

  const getTypeColor = (type) => {
    const colors = {
      'pokemon_normal': 'bg-blue-100 text-blue-800',
      'pokemon_gigantamax': 'bg-pink-100 text-pink-800',
      'pokemon_mega': 'bg-yellow-100 text-yellow-800',
      'max_counters': 'bg-red-100 text-red-800',
      'max_defenders': 'bg-green-100 text-green-800',
      'raid_counters': 'bg-purple-100 text-purple-800',
      'unknown': 'bg-gray-100 text-gray-800'
    };
    return colors[type] || colors.unknown;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'processing':
        return <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (xhrRef.current) {
        xhrRef.current.abort();
      }
    };
  }, []);

  return (
    <div className="space-y-6">
      {/* Notification */}
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center space-x-3 ${
              notification.type === 'success'
                ? 'bg-green-100 text-green-800 border border-green-200'
                : 'bg-red-100 text-red-800 border border-red-200'
            }`}
          >
            {notification.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <span>{notification.message}</span>
            <button onClick={() => setNotification(null)} className="ml-2">
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">CSV Import Manager</h1>
          <p className="text-gray-600 dark:text-gray-400">Upload and import Pokemon data from CSV files</p>
        </div>
        <button
          onClick={handleImportAll}
          disabled={importAllLoading || importing}
          className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
        >
          {importAllLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
          <span>Import All</span>
        </button>
      </div>

      {/* Upload Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Upload CSV File</h2>
        
        {/* Drag & Drop Area */}
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
            dragActive
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
              : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'
          }`}
        >
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            onChange={handleFileInputChange}
            accept=".csv"
          />
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400 mb-2">
            Drag and drop a file here or
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="text-blue-600 hover:underline ml-1"
            >
              browse
            </button>
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500">
            CSV files only, max {MAX_FILE_SIZE_MB}MB
          </p>
          {selectedFile && (
            <div className="mt-4 flex items-center justify-center space-x-2 text-green-600 dark:text-green-400">
              <FileText className="w-5 h-5" />
              <span>{selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)</span>
            </div>
          )}
        </div>

        {/* Import Options */}
        {selectedFile && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="importType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Import Type
              </label>
              <select
                id="importType"
                value={importType}
                onChange={(e) => setImportType(e.target.value)}
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={importing || importAllLoading || previewLoading}
              >
                <option value="auto">Auto-detect</option>
                <option value="pokemon_normal">Pokemon Data</option>
                <option value="pokemon_gigantamax">Gigantamax Pokemon</option>
                <option value="pokemon_mega">Mega Pokemon</option>
                <option value="max_counters">Max Battle Counters</option>
                <option value="max_defenders">Max Battle Defenders</option>
                <option value="raid_counters">Raid Counters</option>
              </select>
            </div>
            <div className="flex items-center mt-7">
              <input
                type="checkbox"
                id="clearExisting"
                checked={clearExisting}
                onChange={(e) => setClearExisting(e.target.checked)}
                className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                disabled={importing || importAllLoading || previewLoading}
              />
              <label htmlFor="clearExisting" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Clear existing data before import
              </label>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {selectedFile && (
          <div className="mt-6 flex space-x-4">
            <button
              onClick={handlePreview}
              disabled={previewLoading || importing || importAllLoading}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors"
            >
              {previewLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Eye className="w-5 h-5" />}
              <span>Preview</span>
            </button>
            <button
              onClick={handleImport}
              disabled={!selectedFile || importing || importAllLoading || previewLoading}
              className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {importing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
              <span>
                {importStatus === 'uploading' ? 'Uploading...' :
                 importStatus === 'validating' ? 'Validating...' :
                 importStatus === 'importing' ? 'Importing...' :
                 importStatus === 'completed' ? 'Completed' :
                 importStatus === 'failed' ? 'Failed' :
                 'Import File'}
              </span>
            </button>
          </div>
        )}

        {/* Upload Progress */}
        {importStatus === 'uploading' && uploadProgress > 0 && (
          <div className="mt-4">
            <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
              <span>Uploading...</span>
              <span>{Math.round(uploadProgress)}%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Import Progress */}
        {importProgress && (
          <div className="mt-4">
            <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
              <span>Processing rows...</span>
              <span>{importProgress.processed} / {importProgress.total}</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="bg-green-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(importProgress.processed / importProgress.total) * 100}%` }}
              />
            </div>
            <div className="mt-2 grid grid-cols-3 gap-4 text-xs text-gray-600 dark:text-gray-400">
              <div>Imported: <span className="font-semibold text-green-600">{importProgress.imported}</span></div>
              <div>Failed: <span className="font-semibold text-red-600">{importProgress.failed}</span></div>
              <div>Skipped: <span className="font-semibold text-yellow-600">{importProgress.skipped}</span></div>
            </div>
          </div>
        )}
      </div>

      {/* CSV Preview */}
      {showPreview && previewData && (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">CSV Preview</h2>
            <button
              onClick={() => setShowPreview(false)}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Validation Summary */}
          {previewData.validation && (
            <div className={`mb-4 p-4 rounded-lg ${
              previewData.validation.valid
                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
            }`}>
              <div className="flex items-center space-x-2 mb-2">
                {previewData.validation.valid ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-600" />
                )}
                <span className={`font-semibold ${
                  previewData.validation.valid ? 'text-green-800' : 'text-red-800'
                }`}>
                  {previewData.validation.valid ? 'CSV is valid' : 'CSV validation failed'}
                </span>
              </div>
              {previewData.validation.errors && previewData.validation.errors.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm font-medium text-red-800 mb-1">Errors:</p>
                  <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                    {previewData.validation.errors.slice(0, 5).map((error, idx) => (
                      <li key={idx}>{typeof error === 'string' ? error : `${error.column}: ${error.error}`}</li>
                    ))}
                    {previewData.validation.errors.length > 5 && (
                      <li>... and {previewData.validation.errors.length - 5} more errors</li>
                    )}
                  </ul>
                </div>
              )}
              {previewData.validation.warnings && previewData.validation.warnings.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm font-medium text-yellow-800 mb-1">Warnings:</p>
                  <ul className="list-disc list-inside text-sm text-yellow-700 space-y-1">
                    {previewData.validation.warnings.slice(0, 3).map((warning, idx) => (
                      <li key={idx}>{typeof warning === 'string' ? warning : warning.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Preview Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  {previewData.preview && previewData.preview.length > 0 && 
                    Object.keys(previewData.preview[0].data).map((header, idx) => (
                      <th
                        key={idx}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                      >
                        {header}
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {previewData.preview && previewData.preview.map((row, idx) => (
                  <tr
                    key={idx}
                    className={row.hasErrors ? 'bg-red-50 dark:bg-red-900/20' : ''}
                  >
                    {Object.values(row.data).map((value, cellIdx) => (
                      <td
                        key={cellIdx}
                        className="px-4 py-2 text-sm text-gray-900 dark:text-white"
                      >
                        {String(value || '').substring(0, 50)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Showing {previewData.previewRows} of {previewData.totalRows} rows
          </p>
        </div>
      )}

      {/* Files in System */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Files in System</h2>
          <button onClick={fetchFiles} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : files.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {files.map((file, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="flex items-center space-x-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg"
              >
                <FileSpreadsheet className="w-6 h-6 text-blue-500" />
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">{file.name}</p>
                  <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${getTypeColor(file.type)}`}>
                    {getTypeLabel(file.type)}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-500 dark:text-gray-400 py-8">
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No CSV files found in data directory</p>
          </div>
        )}
      </div>

      {/* Import History */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Import History</h2>
          <button onClick={fetchHistory} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : importHistory.length > 0 ? (
          <div className="space-y-4">
            {importHistory.map((record) => (
              <div key={record.id} className="flex items-start space-x-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                {getStatusIcon(record.import_status || record.status)}
                <div className="flex-1">
                  <p className="font-medium text-gray-900 dark:text-white">
                    Import {getTypeLabel(record.file_type)} ({record.file_name})
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Status: <span className={`font-semibold ${
                      (record.import_status || record.status) === 'completed' ? 'text-green-600' :
                      (record.import_status || record.status) === 'failed' ? 'text-red-600' :
                      'text-blue-600'
                    }`}>
                      {record.import_status || record.status}
                    </span>
                    {(record.import_status || record.status) === 'completed' && ` (${record.imported_rows || 0}/${record.total_rows || 0} rows)`}
                    {(record.import_status || record.status) === 'failed' && record.failed_rows > 0 && (
                      <span className="ml-2">
                        <button
                          onClick={() => handleDownloadErrorReport(record.id)}
                          className="text-blue-600 hover:underline flex items-center space-x-1"
                        >
                          <Download className="w-4 h-4" />
                          <span>Download Error Report</span>
                        </button>
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    Started: {new Date(record.started_at).toLocaleString()}
                    {record.completed_at && `, Completed: ${new Date(record.completed_at).toLocaleString()}`}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-500 dark:text-gray-400 py-8">
            <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No import history</p>
          </div>
        )}
      </div>

      {/* Supported File Types Info */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-3 mb-4">
          <Info className="w-6 h-6 text-blue-500" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Supported File Types</h2>
        </div>
        <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
          <li><strong>pokemon_normal</strong>: Base Pokemon data (ID, Name, Type, Stats)</li>
          <li><strong>pokemon_gigantamax</strong>: Gigantamax Pokemon data</li>
          <li><strong>pokemon_mega</strong>: Mega Evolution Pokemon data</li>
          <li><strong>max_counters</strong>: Counter Pokemon for Max Battle</li>
          <li><strong>max_defenders</strong>: Defender Pokemon for Max Battle</li>
          <li><strong>raid_counters</strong>: Counter Pokemon for Raids</li>
        </ul>
      </div>
    </div>
  );
};

export default CsvImportManager;
